import React, { useEffect, useRef, useState } from 'react';
import { ErrorMessage, Field, FormikProps, getIn } from 'formik';
import clsx from 'clsx';
import { ToolTipUI } from '../UI/ToolTipUI';
import { ToolTipUIForModal } from '../UI/ToolTipUIForModal';
import Select from 'react-select';

// Define the interface for the option object
interface Option {
  id: number;
  name?: string;
  value: string;
}

// Define the interface for the props
interface InputWithPhoneProps {
  fieldName: string;
  label?: string;
  toolTipText?: string;
  placeholder: string;
  placeholderSelect: string;
  formik: FormikProps<any>; // Adjust this type as needed based on your Formik setup
  options: Option[];
  margin?: string;
  isFieldArray?: boolean;
  isTooltipNotRequired?: boolean;
  width?: number;
  isStarRequired?: boolean;
  isSearchIcon?: boolean;
  disabled?: boolean;
  isTooltipForModal?: boolean;
  selectOptionFieldName: string;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  ariaLabel?: string
}

const FieldArrayErrorMessage: React.FC<{ name: string }> = ({ name }) => (
  <Field name={name}>
    {({ form }: { form: any }) => {
      const error = getIn(form.errors, name);
      const touched = getIn(form.touched, name);
      return touched && error ? <div className='text-danger'>{error}</div> : null;
    }}
  </Field>
);

export const InputPeriod: React.FC<InputWithPhoneProps> = ({
  fieldName,
  label,
  toolTipText,
  placeholder,
  placeholderSelect,
  formik,
  options,
  margin,
  isFieldArray,
  isTooltipNotRequired,
  width,
  isStarRequired,
  isSearchIcon,
  disabled,
  isTooltipForModal,
  selectOptionFieldName,
  onFocus,
  onBlur,
  ariaLabel
}) => {
  // State to manage the open/closed state of the Select component
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  // Convert options for react-select
  const selectOptions = options.map((option) => ({
    id:option.id,
    value: option.value,
    label: option.name
  }));

  // Handle change event for the select component
  const handleChange = (option: any) => {
    formik.setFieldValue(selectOptionFieldName, option ? option.value : ''); // Set the value of the text field to match the dial code
    setIsSelectOpen(false); // Close the dropdown after selection
  };

  // Handle change event for the text input field
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(fieldName, event.target.value);
  };

  // Get the currently selected option
  const selectedOption = selectOptions.find(
    (option) => option.value === formik.values[selectOptionFieldName]
  );

  // Effect to handle clicks outside the Select component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`fv-row mb-7 ${margin || ''} ${width ? `col-md-${width}` : ''}`}>
      {label && (
        <label className='d-flex form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0 '>
          {label}
          {!isTooltipNotRequired && (
            <>
              {!isStarRequired && '*'}
              {isTooltipForModal ? (
                <ToolTipUIForModal tooltipText={toolTipText} />
              ) : (
                <ToolTipUI tooltipText={toolTipText} />
              )}
            </>
          )}
        </label>
      )}

      <div ref={selectRef} className='d-flex flex-column align-items-start gap-2 w-100'>
        <div className='d-flex align-items-center w-100 gap-2'>
          <div className='w-25'>
            <Select
              name={selectOptionFieldName}
              options={selectOptions}
              className={clsx('react-select-container', {
                'is-invalid-local': formik.touched[fieldName] && formik.errors[fieldName],
                'is-valid-local': formik.touched[fieldName] && !formik.errors[fieldName],
              })}
              classNamePrefix='react-select'
              onChange={handleChange}
              onBlur={onBlur}
              onFocus={onFocus}
              aria-label={ariaLabel}
              onMenuOpen={() => setIsSelectOpen(true)}
              onMenuClose={() => setIsSelectOpen(false)}
              placeholder={placeholderSelect}
              filterOption={(candidate: any, input) => {
                return candidate?.data?.value.includes(input)
              }

              }
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: '100%',
                  padding: '0',
                }),
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: '0.5rem',
                  border: state.isFocused || formik.touched[fieldName] ? '1px solid #ddd' : '',
                  boxShadow: 'none',
                  '&:hover': {
                    border: '1px solid #ddd',
                  },
                  minHeight: '40px',
                  height: '40px',
                  width: '100%',
                  fontSize: '12px',
                  fontWeight: '500',
                }),
                menu: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                  width: '100%', // Set the width of the dropdown menu
                }),
                menuList: (provided) => ({
                  ...provided,
                  width: '100%', // Ensure the menu list width matches the dropdown
                }),
                option: (provided) => ({
                  ...provided,
                  display: 'flex',
                  flexDirection: 'column',
                }),
              }}
              value={selectedOption}
              menuIsOpen={isSelectOpen}
              isDisabled={disabled}
            />
          </div>

          <input
            type='number'
            name={fieldName}
            disabled={disabled}
            onBlur={(e) => {
              
              formik.setFieldTouched(fieldName, true);
              setIsSelectOpen(false); // Close the dropdown on blu
              onBlur && onBlur(e); // Call the onBlur
            }}

            onFocus={onFocus}
            aria-label={ariaLabel}
            className={clsx('form-control h-40px font-size-12 w-75 px-3', {
              'is-invalid-local':
                formik.touched[fieldName] && formik.errors[fieldName],
              'is-valid-local':
                formik.touched[fieldName] && !formik.errors[selectOptionFieldName],
            })}
            style={{
              borderRadius: '0.5rem',
              border: '1px solid #ddd',
            }}
            value={formik.values[fieldName] || ''}
            onChange={handleInputChange} // Handle user input
            placeholder={placeholder}
          />
        </div>
      </div>

      <div className='text-danger mt-2'>
        {isFieldArray ? (
          <FieldArrayErrorMessage name={selectOptionFieldName} />
        ) : (
          <ErrorMessage name={selectOptionFieldName} />
        )}
      </div>

      <div className='text-danger mt-2'>
        {isFieldArray ? (
          <FieldArrayErrorMessage name={fieldName} />
        ) : (
          <ErrorMessage name={fieldName} />
        )}
      </div>
    </div>
  );
};